// #region imports
import "../styles/SalesOrders.css"

import { useEffect, useState } from "react"

import Page from "../components/Page"

import useFetchWithMsal from "../hooks/useFetchWithMsal"
import { getStates } from "../utils/salesUtils"
import AllocationDialog from "../components/AllocationDialog"

import CloseIcon from "@mui/icons-material/Close"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"

import { DataGrid, gridClasses, GridToolbar } from "@mui/x-data-grid"
import {
  TextField,
  Button,
  Tooltip,
  Chip,
  Box,
  Dialog,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Typography,
  LinearProgress,
  Stack,
  DialogTitle,
  Skeleton,
  IconButton,
} from "@mui/material"
import { CSVLink } from "react-csv"
// #endregion imports

// TODO - Move this URL to a Environment Variable
const data_URL = "/api/sales/orders?admin=true"

const states = getStates()

const CustomToolbar = (rowsToExport) => {
  return (
    <Stack direction="row" spacing={2} alignItems="center" padding={2}>
      <GridToolbar />
      <CsvDownloadButton
        selectedRows={rowsToExport.rowsToExport}
        style={{ marginLeft: "auto" }}
      >
        TSM Export
      </CsvDownloadButton>
    </Stack>
  )
}

/* ---------- MAIN HELPERS ---------- */

// Explodes a single sales order into multiple rows (one per item)
const explodeOrderToRows = (order) => {
  const isInternational =
    (order.rCountry ?? "").toLowerCase() !== "united states"
  const isCalifornia = (order.rState ?? "").toUpperCase() === "CA"
  const isSample = Boolean(order.salesOrderTypeId?.isSample)

  let customerNo = "01DONATION"
  if (!isSample) {
    customerNo = isInternational ? "01INTL" : isCalifornia ? "01CALS" : "01OSS"
  }

  const common = {
    "Customer No.": customerNo,
    "Order No.": `PORT-${order.user?.name ?? "Unknown"}/${(
      order.dateOfRequest ?? ""
    ).replaceAll("-", ".")}-${order.id}`,
    "Order Date": order.dateOfRequest ?? "",
    "Unit Price": "",
    "Tax Amount": "",
    "Shipping Amount": "",
    "Handling Amount": "",
    "Bill to Name": "O'Neill Vintners & Distillers",
    "Bill to Address 1": "8418 S Lac Jac Ave",
    "Bill to Address 2": "",
    "Bill to City": "Parlier",
    "Bill to State": "CA",
    "Bill to Zip": "93648",
    "Bill to Country": "United States",
    "Ship to Name": order?.rName || "",
    "Ship to Address 1": order?.rStreet || "",
    "Ship to City": order?.rCity || "",
    "Ship to State": order?.rState || "",
    "Ship to Zip": order?.rZip || "",
    "Ship to Country": order?.rCountry || "",
    "Ship Date": order.requiredDeliveryDate ?? "",
    "Carrier Code": "",
    "Payment Type": "",
    "Credit Card #": "",
    "Credit Card Expiration": "",
    "Credit Card V/Code": "",
    "Phone #": "",
    "Cell Phone #": "",
    "Email Address": order.rEmail ?? "",
    "Wine Club Member #": "",
    "Ref/PO#": `PORT-${order.user?.name ?? "Unknown"}/${(
      order.dateOfRequest ?? ""
    ).replaceAll("-", ".")}-${order.id}`,
    Comments: order.notes ?? "",
    "TTB Line": isInternational ? "855" : "B8",
    "Sample Flag": isSample ? "Y" : "N",
    Confirm: "",
  }

  return order.salesOrderItems.flatMap((item) => {
    const lots = item.salesOrderItemLots || []

    // If no lots, fallback to legacy logic
    if (lots.length === 0) {
      return [
        {
          ...common,
          "Product Code": item.productId || "",
          Quantity: item.cases || item.bottles || 0,
          "Unit Price": item.unitPrice || 0,
          Lotcode: item.lotCode || item.inventoryLotCode || "N/A",
          "Warehouse Code": item.warehouse || "UNKNOWN",
        },
      ]
    }

    // Map each allocated lot to its own row
    return lots.map((lot) => {
      // Extract the product code from the lot id, e.g., '1021PINLI7512WR-06182024'
      return {
        ...common,
        "Product Code": item.sku || "",
        Quantity: lot.quantity || 0,
        "Unit Price": item.unitPrice || 0,
        Lotcode: lot.lot || "N/A",
        "Warehouse Code": lot.warehouse || "UNKNOWN",
      }
    })
  })
}

// Flattens all selected orders into a single CSV-compatible array
const formatOrdersToCsv = (selectedRows) =>
  selectedRows.flatMap(explodeOrderToRows)

// CSV Export Button
export const CsvDownloadButton = ({ selectedRows }) => {
  const csvData = formatOrdersToCsv(selectedRows)
  const filename = `SalesOrders_${Date.now()}.csv`

  const headers = [
    "Customer No.",
    "Order No.",
    "Order Date",
    "Product Code",
    "Quantity",
    "Unit Price",
    "Tax Amount",
    "Shipping Amount",
    "Handling Amount",
    "Bill to Name",
    "Bill to Address 1",
    "Bill to Address 2",
    "Bill to City",
    "Bill to State",
    "Bill to Zip",
    "Bill to Country",
    "Ship to Name",
    "Ship to Address 1",
    "Ship to City",
    "Ship to State",
    "Ship to Zip",
    "Ship to Country",
    "Ship Date",
    "Carrier Code",
    "Paymnent Type",
    "Credit Card #",
    "Credit Card Expiration",
    "Credit Card V/Code",
    "Phone #",
    "Cell Phone #",
    "Email Address",
    "Wine Club Member #",
    "Lotcode",
    "Discount Amount",
    "Ref/PO#",
    "Warehouse Code",
    "Comments",
    "TTB Line",
    "Sample Flag",
    "Confirm",
  ].map((label) => ({ label, key: label }))

  return (
    <CSVLink
      data={csvData}
      headers={headers}
      filename={filename}
      enclosingCharacter={`"`}
      className={
        !selectedRows || selectedRows.length === 0
          ? "btn btn-secondary disabled"
          : "btn btn-primary"
      }
    >
      TSM Export
    </CSVLink>
  )
}

export const SalesOrdersAdminPage = () => {
  const [changeLogPagination, setChangeLogPagination] = useState({
    pageSize: 5,
    page: 0,
  })
  const [focusedRow, setFocusedRow] = useState({})
  const [focusedProduct, setFocusedProduct] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])
  const [editableValues, setEditableValues] = useState({})

  const focusedAdditionalInfo = focusedRow?.additionalInfo
    ? JSON.parse(focusedRow.additionalInfo)
    : null

  const { execute: executeGetOrders, data, isLoading } = useFetchWithMsal()
  const { execute: executeGetOrderById } = useFetchWithMsal()
  const { execute: executeUpdateSalesOrder, isLoading: posting } = useFetchWithMsal()
  // const { execute: executeUpdateLineItem, isLoading: postingLineItem } =
  //   useFetchWithMsal()
  const {
    execute: executeGetReferenceData,
    data: salesReferenceData,
    isLoading: isLoadingReferenceData,
  } = useFetchWithMsal()

  useEffect(() => {
    if (!salesReferenceData && !isLoadingReferenceData) {
      executeGetReferenceData("GET", "/api/sales/md")
    }
  }, [executeGetReferenceData, salesReferenceData, isLoadingReferenceData])

  useEffect(() => {
    if (!data && !isLoading) {
      executeGetOrders("GET", data_URL)
    }
    console.log("Sales Orders Data:", data)
  }, [executeGetOrders, data, isLoading])

  const ParentChildGrid = ({
    salesOrderItems,
    setFocusedProduct,
    salesReferenceData,
  }) => {
    const [expandedRowIds, setExpandedRowIds] = useState([])

    const toggleExpand = (rowId) => {
      setExpandedRowIds((prev) =>
        prev.includes(rowId)
          ? prev.filter((id) => id !== rowId)
          : [...prev, rowId]
      )
    }

    const rows = salesOrderItems.flatMap((item) => {
      const parentRowId = `parent-${item.id}`
      const isExpanded = true
      const quantity = item.cases || item.bottles || 0
      const allocated = (item.salesOrderItemLots || []).reduce(
        (sum, l) => sum + l.quantity,
        0
      )
      const isOverAllocated = allocated > quantity

      const parentRow = {
        id: parentRowId,
        isParent: true,
        sku: item.sku,
        quantity,
        type: "",
        overAllocated: isOverAllocated,
      }

      const childRows =
        isExpanded && item.salesOrderItemLots?.length
          ? item.salesOrderItemLots.map((lot, idx) => ({
              id: `${parentRowId}-child-${idx}`,
              isParent: false,
              lot: lot.lot || "—",
              type: lot.type || "—",
              warehouse: lot.warehouse || "—",
              quantity: lot.quantity || lot.usedCases || lot.usedBottles || 0,
            }))
          : []

      return [parentRow, ...childRows]
    })

    const columns = [
      {
        field: "sku",
        headerName: "Product / Lot",
        width: 250,
        renderCell: (params) => {
          if (params.row.isParent) {
            const product = salesOrderItems.find(
              (item) => `parent-${item.id}` === params.row.id
            )
            const quantity = product?.cases || product?.bottles || 0
            const allocated = (product?.salesOrderItemLots || []).reduce(
              (sum, l) => sum + l.quantity,
              0
            )
            const overAllocated = allocated > quantity

            return (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Button
                  size="small"
                  onClick={() => toggleExpand(params.row.id)}
                >
                  {expandedRowIds.includes(params.row.id) ? "▼" : "▶"}
                </Button>
                <Typography variant="body2" sx={{ ml: 1 }}>
                  {params.row.sku}
                </Typography>
                {overAllocated && (
                  <Tooltip title={`Overallocated: ${allocated} / ${quantity}`}>
                    <WarningAmberIcon
                      sx={{ ml: 1, color: "goldenrod", fontSize: "1.2rem" }}
                    />
                  </Tooltip>
                )}
              </Box>
            )
          } else {
            return (
              <Typography variant="body2" sx={{ pl: 5 }}>
                {params.row.lot}
              </Typography>
            )
          }
        },
      },
      {
        field: "type",
        headerName: "Requested Type",
        width: 150,
        renderCell: (params) => {
          if (params.row.isParent) {
            const isBottle = params?.row?.sku?.slice(-4, -2) === "01"
            return isBottle == null ? "N/A" : isBottle ? "BOTTLE" : "CASE"
          } else {
            return "—"
          }
        },
      },
      {
        field: "warehouse",
        headerName: "Warehouse",
        width: 120,
        renderCell: (params) =>
          params.row.isParent ? "—" : params.row.warehouse,
      },
      {
        field: "quantity",
        headerName: "Allocated Quantity",
        width: 160,
        renderCell: (params) =>
          params.row.isParent
            ? `Total Required: ${params.row.quantity}`
            : params.row.quantity,
      },
      {
        field: "source",
        headerName: "Source Type",
        width: 120,
        renderCell: (params) => {
          if (params.row.isParent) {
            return "—"
          } else {
            return params.row.type
          }
        },
      },
    ]

    return (
      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        hideFooter
        disableColumnMenu
        getRowClassName={(params) =>
          params.row.isParent ? "parent-row" : "child-row"
        }
        onRowClick={(params) => {
          if (params.row.isParent) {
            const product = salesOrderItems.find(
              (item) => `parent-${item.id}` === params.row.id
            )

            if (product) {
              const matchingLots =
                salesReferenceData?.inventoryOnHand?.filter((lot) => {
                  const baseSku = product.sku?.slice(0, -4)
                  const suffix = product.sku?.slice(-2)
                  const lotBase = lot.ProductNo?.slice(0, -4)
                  const lotSuffix = lot.ProductNo?.slice(-2)
                  return lotBase === baseSku && lotSuffix === suffix
                }) || []

              setFocusedProduct({
                ...product,
                matchingLots,
              })
            }
          }
        }}
        sx={{
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            display: "none",
          },
          "& .child-row": {
            backgroundColor: "#fafafa",
            "&:hover": {
              backgroundColor: "#f0f0f0",
            },
          },
        }}
      />
    )
  }

  const updateSalesOrder = async (id, data) => {
    try {
      const resp = await executeUpdateSalesOrder(
        "POST",
        `/api/sales/update/${id}`,
        data
      )

      if (resp.error) {
        alert("Error updating order")
        return
      }

      // Ensure we have updated lots in the correct item
      const updatedItems = (focusedRow.salesOrderItems || []).map((item) => {
        if (item.id === data.salesOrderItemLots?.[0]?.salesOrderItemId) {
          return {
            ...item,
            salesOrderItemLots: data.salesOrderItemLots,
          }
        }
        return item
      })

      setFocusedRow((prev) => ({
        ...prev,
        ...resp, // full refresh from API if resp is the full salesOrder
        salesOrderItems: updatedItems,
      }))
    } catch (e) {
      alert("Error updating order")
      return
    }

    // Optional: refresh the table or cached orders
    executeGetOrders("GET", data_URL)
  }

  useEffect(() => {
    if (focusedRow) {
      setEditableValues({
        salesorderstatusId: focusedRow.salesorderstatusId || "",
        salesOrderTypeId: focusedRow.salesOrderTypeId || {},
        warehouseId: focusedRow.warehouseId || "Inactive",
        trackingNumber: focusedRow.trackingNumber || "",
        distributor: focusedRow.distributor || "",
        rName: focusedRow.rName || "",
        rStreet: focusedRow.rStreet || "",
        rCity: focusedRow.rCity || "",
        rState: focusedRow.rState || "",
        rZip: focusedRow.rZip || "",
        rCountry: focusedRow.rCountry || "",
        notes: focusedRow.notes || "",
        rEmail: focusedRow.rEmail || "",
      })
    }
  }, [focusedRow])

  const editableFields = Object.keys(editableValues)

  return (
    <Page fullHeight className="SalesOrdersPage">
      {(isLoading || posting) && (
        <div className="loadingBar">
          <LinearProgress />
        </div>
      )}
      <div className="ribbon">
        <Stack direction="column" spacing={1} width={"100%"}>
          <div>
            All Orders - Click on a row to see the details and change status
          </div>
          <h5 style={{ textAlign: "center", margin: 0 }}>Sales Orders Admin</h5>
        </Stack>
        <Stack direction="row" spacing={1}>
          <div></div>
        </Stack>
      </div>
      <div className="body">
        <div
          style={{
            width: "100%",
            height: "100%",
            flex: 1,
            maxHeight: "100%",
          }}
          className="gridContainer"
        >
          <DataGrid
            rows={data || []}
            columns={[
              {
                field: "id",
                headerName: "ID",
                width: 60,
              },
              {
                field: "dateOfRequest",
                headerName: "Submitted",
                width: 100,
              },
              {
                field: "requiredDeliveryDate",
                headerName: "Delivery",
                width: 100,
              },
              {
                field: "salesorderstatusId",
                headerName: "Status",
                width: 140,
                renderCell: (params) => {
                  const status = params.row?.salesorderstatusId
                  const color =
                    status === 2 || status === 7
                      ? "warning"
                      : status === 1
                      ? "success"
                      : status === 5
                      ? "info"
                      : "error"
                  return (
                    <Chip
                      color={color}
                      label={
                        salesReferenceData?.salesOrderStatuses.find(
                          (status) =>
                            status.id === params.row?.salesorderstatusId
                        )?.name ?? "Unknown Status"
                      }
                      size="small"
                    />
                  )
                },
              },
              {
                field: "user",
                headerName: "Requestor",
                width: 140,
                valueGetter: (params) => {
                  return params.row?.user?.name
                },
              },
              {
                field: "rName",
                headerName: "Recipient",
                maxWidth: 350,
                flex: 1,
              },
              {
                field: "products",
                headerName: "Products",
                width: 350,
                renderCell: (params) => {
                  const items = params.row?.salesOrderItems || []
                  const productNames = items.map((item) => item.sku)

                  const previewCount = 2 // Number of products to show before "+N more"
                  const preview = productNames.slice(0, previewCount)
                  const hiddenCount = productNames.length - preview.length

                  const tooltipTitle = productNames.join(", ")

                  return (
                    <Tooltip title={tooltipTitle}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {preview.map((name, index) => (
                          <Chip key={index} label={name} size="small" />
                        ))}
                        {hiddenCount > 0 && (
                          <Chip label={`+${hiddenCount} more`} size="small" />
                        )}
                      </Box>
                    </Tooltip>
                  )
                },
              },
              {
                field: "rState",
                headerName: "State",
                width: 100,
              },
              {
                field: "lastModified",
                headerName: "Last Modified By",
                width: 150,
                valueGetter: (params) => {
                  // Extract change log from row
                  const changes = params.row?.changeLogs || []

                  // Get the last modified entry (latest by date)
                  const lastChange = changes.length
                    ? changes.reduce((latest, current) =>
                        new Date(latest.changedAt) > new Date(current.changedAt)
                          ? latest
                          : current
                      )
                    : null

                  return lastChange?.user || "N/A" // Display last modified user or "N/A"
                },
              },
            ]}
            sortModel={[
              {
                field: "id",
                sort: "desc",
              },
            ]}
            disableDensitySelector
            slotProps={{
              toolbar: {},
            }}
            slots={{
              toolbar: () => <CustomToolbar rowsToExport={selectedRows} />,
            }}
            pagination
            getRowHeight={() => "auto"}
            getEstimatedRowHeight={() => 40}
            columnHeaderHeight={40}
            pageSizeOptions={[100]}
            checkboxSelection
            disableRowSelectionOnClick
            onRowSelectionModelChange={(selectedRows) => {
              setSelectedRows(
                selectedRows.map(
                  (rowId) => data.find((row) => row.id === rowId) || {}
                )
              )
            }}
            sx={{
              [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]:
                {
                  outline: "none",
                },
              [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
                {
                  outline: "none",
                },
            }}
              // safer click handler
              onRowClick={async (params) => {
                const id = params?.row?.id;
                if (!id) {
                  console.warn("No row id on click:", params);
                  return;
                }

                try {
                  const order = await executeGetOrderById("GET", data_URL+`&id=${id}`);
                  if (!order) {
                    console.warn("No order returned for id:", id);
                    setFocusedRow({});
                    return;
                  }
                  setFocusedRow(order);
                } catch (err) {
                  console.error("Failed to load order:", err);
                  setFocusedRow({});
                  // optionally show a toast here
                }
              }}
          />
        </div>
      </div>
      {focusedRow?.id && (
        <Dialog
          fullWidth
          maxWidth="lg"
          open={Boolean(focusedRow?.id)}
          PaperProps={{
            sx: {
              overflow: "hidden", // prevents the internal layout from spilling out
              maxWidth: "100vw", // ensures it doesn't exceed viewport width
            },
          }}
          onClose={() => {
            setFocusedRow({})
            setIsEditing(false)
          }}
        >
          {/* Top Left Close Button */}
          <IconButton
            onClick={() => {
              setFocusedRow({})
              setIsEditing(false)
            }}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              color: "grey.700",
              zIndex: 1000, // Ensure it appears above other content
              "&:hover": {
                backgroundColor: "grey.300", // No background on hover
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            sx={{ p: 2, overflowY: "auto", maxHeight: "90vh", width: "100%" }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#f5f5f5",
                padding: "16px 24px",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <Box>
                <Typography variant="h6">
                  Order #{focusedRow.id} -{" "}
                  {salesReferenceData?.salesOrderTypes.find(
                    (type) => type.id === focusedRow?.salesOrderTypeId
                  )?.description ?? "Unknown Type"}
                </Typography>
                <Typography variant="subtitle1">
                  Requested by: {focusedRow.user?.name || "Unknown"}
                </Typography>
                <Typography variant="subtitle2">
                  {focusedRow?.pickupOrder ? "Pickup Order" : "Shipping Order"}
                </Typography>
                <Typography variant="subtitle2">
                  Order Created: {focusedRow.dateOfRequest}
                </Typography>
              </Box>
              <div style={{ display: "flex", gap: "10px" }}>
                <Button
                  variant={isEditing ? "contained" : "outlined"}
                  onClick={() => {
                    if (isEditing) {
                      // Check for changes and update only if needed
                      const changedFields = {}

                      editableFields.forEach((field) => {
                        if (focusedRow[field] !== editableValues[field]) {
                          changedFields[field] = editableValues[field]
                        }
                      })

                      if (Object.keys(changedFields).length > 0) {
                        updateSalesOrder(focusedRow.id, changedFields)
                        setFocusedRow({ ...focusedRow, ...changedFields })
                      }
                    }
                    setIsEditing(!isEditing)
                  }}
                >
                  {isEditing ? "Save" : "Edit"}
                </Button>
                <Button
                  variant="outlined"
                  disabled={!isEditing}
                  color="error"
                  onClick={() => {
                    // Reset changes
                    setEditableValues({
                      salesorderstatusId: focusedRow.salesorderstatusId || "",
                      salesOrderTypeId: focusedRow.salesOrderTypeId || {},
                      warehouseId: focusedRow.warehouseId || "Inactive",
                      trackingNumber: focusedRow.trackingNumber || "",
                      distributor: focusedRow.distributor || "",
                      rName: focusedRow.rName || "",
                      rStreet: focusedRow.rStreet || "",
                      rCity: focusedRow.rCity || "",
                      rState: focusedRow.rState || "",
                      rZip: focusedRow.rZip || "",
                      rCountry: focusedRow.rCountry || "",
                      notes: focusedRow.notes || "",
                      rEmail: focusedRow.rEmail || "",
                    })
                    setIsEditing(false)
                  }}
                >
                  Cancel
                </Button>
              </div>
            </DialogTitle>
            <Box style={{ display: "block", padding: "10px" }}>
              <Box p={2}>
                <Box display="flex" flexDirection="row" gap={2}>
                  {/* Left Column */}
                  <Box flex={1} minWidth={0}>
                    <TextField
                      label="Order #"
                      value={focusedRow.id}
                      fullWidth
                      size="small"
                      InputProps={{ readOnly: true }}
                    />
                    <TextField
                      label="Requestor"
                      value={focusedRow.user?.name || ""}
                      fullWidth
                      size="small"
                      sx={{ mt: 1 }}
                      InputProps={{ readOnly: true }}
                    />
                    <TextField
                      label="Distributor"
                      value={editableValues.distributor || ""}
                      fullWidth
                      size="small"
                      sx={{ mt: 1 }}
                      onChange={(e) =>
                        setEditableValues({
                          ...editableValues,
                          distributor: e.target.value,
                        })
                      }
                      disabled={!isEditing}
                    />
                    <TextField
                      label="Requested Delivery Date"
                      value={focusedRow.requiredDeliveryDate}
                      fullWidth
                      size="small"
                      sx={{ mt: 1 }}
                      InputProps={{ readOnly: true }}
                    />
                  </Box>

                  {/* Right Column */}
                  <Box flex={1} minWidth={0}>
                    <Box display="flex" gap={2} alignItems="center">
                      <FormControl size="small" fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          label="Status"
                          value={editableValues.salesorderstatusId || ""}
                          onChange={(e) => {
                            if (focusedRow.trackingNumber === "" && e.target.value === 5) {
                              alert("Setting status to 'Shipped' Without a tracking number")
                            }
                              setEditableValues({
                                ...editableValues,
                                salesorderstatusId: e.target.value,
                              })
                          }
                        }
                          disabled={!isEditing}
                        >
                          {salesReferenceData?.salesOrderStatuses?.map(
                            (status) => (
                              <MenuItem key={status.id} value={status.id}>
                                {status.name}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box mt={1} display="flex" gap={1} flexDirection={"column"}>
                      <TextField
                        select
                        label="Order Type"
                        value={editableValues?.salesOrderTypeId ?? ""}
                        fullWidth
                        size="small"
                        disabled={!isEditing}
                        onChange={(e) =>
                          setEditableValues({
                            ...editableValues,
                            salesOrderTypeId: e.target.value, // just the ID
                          })
                        }
                      >
                        {salesReferenceData?.salesOrderTypes?.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.description}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        label="Recipient Email"
                        value={editableValues?.rEmail}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) =>
                          setEditableValues({
                            ...editableValues,
                            rEmail: e.target.value,
                          })
                        }
                        disabled={!isEditing}
                      />

                      <TextField
                        value={editableValues?.trackingNumber}
                        label="Tracking Number"
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) =>
                          setEditableValues({
                            ...editableValues,
                            trackingNumber: e.target.value,
                          })
                        }
                        disabled={!isEditing}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Bottom Section */}
                <Box display="flex" mt={4} gap={4} flexDirection="row">
                  {/* Left Side */}
                  <Box flex={1} minWidth={0} display="flex" flexDirection="column" gap={2}>
                    <Typography variant="h6">Shipping Info</Typography>
                    <TextField
                      label="Recipient"
                      value={editableValues?.rName || ""}
                      size="small"
                      onChange={(e) =>
                        setEditableValues({
                          ...editableValues,
                          rName: e.target.value,
                        })
                      }
                      disabled={!isEditing}
                    />
                    <TextField
                      label="Street"
                      value={editableValues?.rStreet || ""}
                      size="small"
                      onChange={(e) =>
                        setEditableValues({
                          ...editableValues,
                          rStreet: e.target.value,
                        })
                      }
                      disabled={!isEditing}
                    />
                    <TextField
                      label="City"
                      value={editableValues?.rCity || ""}
                      size="small"
                      onChange={(e) =>
                        setEditableValues({
                          ...editableValues,
                          rCity: e.target.value,
                        })
                      }
                      disabled={!isEditing}
                    />
                    <TextField
                      select
                      label="State"
                      value={editableValues?.rState || ""}
                      size="small"
                      onChange={(e) =>
                        setEditableValues({
                          ...editableValues,
                          rState: e.target.value,
                        })
                      }
                      disabled={!isEditing}
                    >
                      {states.map((state) => (
                        <MenuItem key={state.value} value={state.value}>
                          {state.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="Zip Code"
                      value={editableValues?.rZip || ""}
                      size="small"
                      onChange={(e) =>
                        setEditableValues({
                          ...editableValues,
                          rZip: e.target.value,
                        })
                      }
                      disabled={!isEditing}
                    />
                    <TextField
                      label="Country"
                      value={editableValues?.rCountry || ""}
                      size="small"
                      onChange={(e) =>
                        setEditableValues({
                          ...editableValues,
                          rCountry: e.target.value,
                        })
                      }
                      disabled={!isEditing}
                    />
                    <TextField
                      label="Notes"
                      value={editableValues?.notes || ""}
                      multiline
                      minRows={3}
                      size="small"
                      onChange={(e) =>
                        setEditableValues({
                          ...editableValues,
                          notes: e.target.value,
                        })
                      }
                      disabled={!isEditing}
                    />

                    <Box mt={2}>
                      <Typography
                        variant="subtitle2"
                        sx={{ textDecoration: "underline" }}
                      >
                        Additional Files
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        {JSON.parse(focusedRow?.additionalFileUrls || "[]").map(
                          (f) => (
                            <a
                              href={f}
                              key={f}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Download
                            </a>
                          )
                        )}
                      </Box>
                    </Box>

                    <Box mt={2}>
                      <Typography variant="subtitle2">Products</Typography>
                      <ParentChildGrid
                        salesOrderItems={focusedRow.salesOrderItems || []}
                        setFocusedProduct={setFocusedProduct}
                        salesReferenceData={salesReferenceData}
                      />
                    </Box>
                  </Box>

                  {/* Right Side */}
                  <Box flex={1} minWidth={0} display="flex" flexDirection="column" gap={2}>
                    <Typography variant="h6">Compliance Info</Typography>
                    <TextField
                      select
                      label="Event or Retail"
                      value={focusedAdditionalInfo?.eventOrRetail || ""}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="">—</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </TextField>
                    <TextField
                      select
                      label="Event Non Profit"
                      value={focusedAdditionalInfo?.eventNonProfit || ""}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="">—</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </TextField>
                    <TextField
                      label="Non Profit Name"
                      value={focusedAdditionalInfo?.eventNonProfitName || ""}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      select
                      label="Distributor Contacted"
                      value={focusedAdditionalInfo?.distributorContacted || ""}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="">—</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </TextField>
                    <TextField
                      select
                      label="Distributor Availability"
                      value={focusedAdditionalInfo?.distributorAvailability || ""}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="">—</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </TextField>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ textDecoration: "underline" }}
                      >
                        501c3 File Upload:
                      </Typography>
                      {JSON.parse(focusedRow?.taxStatusUrl || "[]").map((f) => (
                        <a
                          href={f}
                          key={f}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download
                        </a>
                      ))}
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" mt={2}>
                        Status Change Log
                      </Typography>
                      {!salesReferenceData?.salesOrderStatuses ? (
                        <Skeleton variant="rectangular" height={400} />
                      ) : (
                        <DataGrid
                          rows={focusedRow.changeLogs || []}
                          columns={[
                            {
                              field: "user",
                              headerName: "Modified By",
                              width: 150,
                            },
                            {
                              field: "changedAt",
                              headerName: "Date Changed",
                              width: 130,
                            },
                            {
                              field: "oldStatus",
                              headerName: "Old Status",
                              width: 150,
                              valueGetter: (params) => {
                                const match =
                                  salesReferenceData.salesOrderStatuses.find(
                                    (s) =>
                                      String(s.id) ===
                                      String(params.row.oldStatus)
                                  )
                                return match?.name ?? "—"
                              },
                            },
                            {
                              field: "newStatus",
                              headerName: "Status",
                              width: 150,
                              valueGetter: (params) => {
                                const match =
                                  salesReferenceData.salesOrderStatuses.find(
                                    (s) =>
                                      String(s.id) ===
                                      String(params.row.newStatus)
                                  )
                                return match?.name ?? "—"
                              },
                            },
                          ]}
                          sortModel={[{ field: "changedAt", sort: "asc" }]}
                          sx={{
                            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar":
                              {
                                display: "none",
                              },
                          }}
                          pagination
                          paginationModel={changeLogPagination}
                          onPaginationModelChange={setChangeLogPagination}
                          pageSizeOptions={[5, 10, 20]}
                          disableRowSelectionOnClick
                          disableColumnMenu
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Dialog>
      )}
      {!!focusedProduct && !!focusedRow && (
        <AllocationDialog
          focusedProduct={focusedProduct}
          setFocusedProduct={setFocusedProduct}
          focusedRow={focusedRow}
          setFocusedRow={setFocusedRow}
          salesReferenceData={salesReferenceData}
          executeUpdateSalesOrder={executeUpdateSalesOrder}
        />
      )}
    </Page>
  )
}